// Mahjong Connect (Shisen-sho; owner 2026-10-31, the second tile game after Mahjong: .ai/next-game-research.md,
// .ai/mahjong-rules-research.md section 6). The 144 tiles lie face up in one flat grid, 18 x 8 on a wide board and
// 8 x 18 on a board taller than wide. Two tiles that match (identical, or any two flowers, or any two seasons) leave
// together for the discard pile when a line of at most three straight pieces (two turns) joins them through empty
// places only; the line may run round the outside of the grid. Won when every tile is gone. Deals are always solvable;
// a player with no move left may shuffle the tiles still on the board (free). Owner's choices: .ai/game-rules.md
// "Mahjong Connect".
//
// Each cell of the grid is a tableau pile of 0 or 1 tile (as Mahjong's places), row by row from the top left. The
// places use Mahjong's Place / Layout (layer 0 everywhere), so the board, the geometry and the tile pictures are
// Mahjong's; `margin` keeps half a tile free round the grid for the line's outside run.
import type { CardKey, GameState, PileRef } from '../types';
import { buildTileDeck } from '../cards';
import { patternForOrder, shuffle } from '../shuffle';
import { card, pileCards, type VariantRules } from './base';
import { shuffleList, tilePairs, tilesMatch, type Layout, type Place } from './mahjong';

/** A grid of `cols` x `rows` cells, in reading order (cell c, r = place r * cols + c). */
function grid(name: Layout['name'], cols: number, rows: number): Layout {
  const places: Place[] = [];
  for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) places.push({ z: 0, x: 2 * c, y: 2 * r });
  return { name, places, w: 2 * cols, h: 2 * rows, layers: 0, margin: 0.5 };
}

/** GameState.layout indexes this list: 0 the wide grid (a computer, a phone held sideways), 1 the tall one (a phone or
 *  a tablet held upright). A saved game keeps its number: add new grids at the end. */
export const CONNECT_LAYOUTS: readonly Layout[] = [grid('wide', 18, 8), grid('tall', 8, 18)];
export const connectLayoutOf = (s: { layout?: number }): Layout => CONNECT_LAYOUTS[s.layout ?? 0] ?? CONNECT_LAYOUTS[0]!;
const colsOf = (l: Layout) => l.w / 2;
const rowsOf = (l: Layout) => l.h / 2;

/** A point of the line: a cell of the grid, or one just outside it (c = -1 or cols, r = -1 or rows). */
export interface Cell {
  readonly c: number;
  readonly r: number;
}

/**
 * The line that joins places `a` and `b` of layout `l`, as its corner cells from `a` to `b` (2 to 4 cells), or null
 * when none may: every cell between the two must be empty (`filled(place)` tells the others), and the line makes at
 * most two turns. Outside the grid every cell is empty (the line may run round the outside). Checked the classic way:
 * a straight line, then one turn (two corners), then two turns (every column and every row the middle piece may run
 * along, the nearest first, so the hint draws the shortest line).
 */
export function connectPath(l: Layout, a: number, b: number, filled: (place: number) => boolean): Cell[] | null {
  if (a === b) return null;
  const cols = colsOf(l);
  const rows = rowsOf(l);
  const A: Cell = { c: a % cols, r: Math.floor(a / cols) };
  const B: Cell = { c: b % cols, r: Math.floor(b / cols) };
  /** An empty cell: outside the grid, or a place without a tile. */
  const free = (c: number, r: number) => c < 0 || r < 0 || c >= cols || r >= rows || !filled(r * cols + c);
  /** Every cell strictly between two cells of one row or one column is empty. */
  const between = (p: Cell, q: Cell): boolean => {
    if (p.c === q.c) {
      for (let r = Math.min(p.r, q.r) + 1; r < Math.max(p.r, q.r); r++) if (!free(p.c, r)) return false;
      return true;
    }
    if (p.r === q.r) {
      for (let c = Math.min(p.c, q.c) + 1; c < Math.max(p.c, q.c); c++) if (!free(c, p.r)) return false;
      return true;
    }
    return false;
  };
  // straight
  if ((A.c === B.c || A.r === B.r) && between(A, B)) return [A, B];
  // one turn: the corner is empty and both pieces are clear
  for (const k of [{ c: A.c, r: B.r }, { c: B.c, r: A.r }]) {
    if (free(k.c, k.r) && between(A, k) && between(k, B)) return [A, k, B];
  }
  // two turns: the middle piece runs down a column (the first corner in A's row, the second in B's row) or along a row
  const spans = (n: number, from: number): number[] => {
    // -1 .. n, the nearest to `from` first
    const out: number[] = [];
    for (let d = 1; d <= n + 1; d++) {
      if (from - d >= -1) out.push(from - d);
      if (from + d <= n) out.push(from + d);
    }
    return out;
  };
  for (const c of spans(cols, A.c)) {
    const k1: Cell = { c, r: A.r };
    const k2: Cell = { c, r: B.r };
    if (c === B.c) continue; // one turn, tried above
    if (free(k1.c, k1.r) && free(k2.c, k2.r) && between(A, k1) && between(k1, k2) && between(k2, B)) return [A, k1, k2, B];
  }
  for (const r of spans(rows, A.r)) {
    const k1: Cell = { c: A.c, r };
    const k2: Cell = { c: B.c, r };
    if (r === B.r) continue;
    if (free(k1.c, k1.r) && free(k2.c, k2.r) && between(A, k1) && between(k1, k2) && between(k2, B)) return [A, k1, k2, B];
  }
  return null;
}

const filledIn = (s: GameState) => (i: number) => !!s.piles.tableau[i]?.length;
/** The line that joins two places of the board now, or null. */
export const pathBetween = (s: GameState, a: number, b: number): Cell[] | null => connectPath(connectLayoutOf(s), a, b, filledIn(s));

/** Every pair of matching tiles a line joins now (place indexes), the hint's and the "no moves" check's. */
export function joinablePairs(s: GameState): Array<[number, number]> {
  const l = connectLayoutOf(s);
  const filled = s.piles.tableau.map((_, i) => i).filter((i) => s.piles.tableau[i]!.length);
  const out: Array<[number, number]> = [];
  for (let a = 0; a < filled.length; a++)
    for (let b = a + 1; b < filled.length; b++) {
      const i = filled[a]!;
      const j = filled[b]!;
      if (!tilesMatch(card(s, s.piles.tableau[i]![0]!).id, card(s, s.piles.tableau[j]![0]!).id)) continue;
      if (connectPath(l, i, j, filledIn(s))) out.push([i, j]);
    }
  return out;
}

export const hasJoin = (s: GameState): boolean => joinablePairs(s).length > 0;

/**
 * A solvable arrangement of `ids` over `places` (ids.length === places.length): the game played backwards. All the
 * places count as filled; again and again two of them a line joins through the places already given out (empty by
 * then in the real game) take a matching pair, so the pairs taken out in that order win the game. A pick that finds
 * no joinable pair starts over (`tries` times); null if it never got through.
 */
export function solvableGrid(ids: readonly string[], places: readonly number[], rng: () => number, layout: Layout, tries = 50): Map<number, string> | null {
  const pairs = tilePairs(ids, rng);
  if (!pairs) return null;
  for (let t = 0; t < tries; t++) {
    const left = new Set(places);
    const out = new Map<number, string>();
    let ok = true;
    for (const [a, b] of pairs) {
      let found: [number, number] | null = null;
      for (const i of shuffleList([...left], rng)) {
        const partners = [...left].filter((j) => j !== i && connectPath(layout, i, j, (p) => left.has(p)));
        if (partners.length) {
          found = [i, partners[Math.floor(rng() * partners.length)]!];
          break;
        }
      }
      if (!found) {
        ok = false;
        break;
      }
      out.set(found[0], a);
      out.set(found[1], b);
      left.delete(found[0]);
      left.delete(found[1]);
    }
    if (ok) return out;
  }
  return null;
}

/** A new deal's tile ids, place by place (the deal turns them into a shuffle pattern, so Retry and the stored wins
 *  work as in the other games). A plain shuffle if the search never got through (it does in practice). */
function dealIds(base: readonly string[], rng: () => number, layout = 0): string[] {
  const l = CONNECT_LAYOUTS[layout] ?? CONNECT_LAYOUTS[0]!;
  const places = l.places.map((_, i) => i);
  const arranged = solvableGrid(base, places, rng, l);
  return arranged ? places.map((i) => arranged.get(i)!) : shuffleList(base, rng);
}

/**
 * A deal of the wide grid on the tall one (the race online, owner 2026-11-02: both seats play the same tiles, a phone
 * held upright on its tall grid): the grid turned over its diagonal, cell (c, r) of the wide grid at (r, c) of the tall
 * one. A line of at most two turns is still one, so the deal stays solvable and as hard. `pattern`: the wide deal's 144
 * numbers and its layout (0); the answer the tall deal's, ending with 1.
 */
export function tallFromWide(pattern: readonly number[]): number[] {
  const base = buildTileDeck().map((c) => c.id);
  const wide = CONNECT_LAYOUTS[0]!;
  const cols = colsOf(wide);
  const rows = rowsOf(wide);
  const { order } = shuffle(base.length, pattern.slice(0, base.length), Math.random);
  const tall: string[] = [];
  // the tall grid row by row: its row r is the wide grid's column r
  for (let r = 0; r < cols; r++) for (let c = 0; c < rows; c++) tall.push(base[order[c * cols + r]!]!);
  return [...patternForOrder(base, tall), 1];
}

/** The tiles still on the board, shuffled over the same places, solvable when it can be: the free shuffle when no
 *  move is left. */
export function shuffledGrid(s: GameState, rng: () => number): Array<readonly CardKey[]> {
  const places = s.piles.tableau.map((_, i) => i).filter((i) => s.piles.tableau[i]!.length);
  const keys = places.map((i) => s.piles.tableau[i]![0]!);
  const ids = keys.map((k) => card(s, k).id);
  const arranged = solvableGrid(ids, places, rng, connectLayoutOf(s));
  const pool = keys.slice();
  const order = arranged ? places.map((i) => arranged.get(i)!) : shuffleList(ids, rng);
  const tableau: Array<readonly CardKey[]> = s.piles.tableau.map(() => []);
  places.forEach((place, n) => {
    const k = pool.findIndex((key) => card(s, key).id === order[n]);
    tableau[place] = [pool[k]!];
    pool.splice(k, 1);
  });
  return tableau;
}

/** The tile at a place (a tableau pile), or undefined. Every tile on the grid may be picked up. */
function tileAt(s: GameState, pile: PileRef): CardKey | undefined {
  return pile.kind === 'tableau' ? s.piles.tableau[pile.index]?.[0] : undefined;
}

function canLift(s: GameState, pile: PileRef, index: number): boolean {
  return index === 0 && pileCards(s, pile).length === 1 && tileAt(s, pile) !== undefined;
}

function canDrop(s: GameState, run: readonly CardKey[], target: PileRef): boolean {
  const key = run[0];
  if (key === undefined || run.length !== 1 || target.kind !== 'tableau') return false;
  const other = tileAt(s, target);
  if (other === undefined || other === key || !tilesMatch(card(s, key).id, card(s, other).id)) return false;
  const from = s.piles.tableau.findIndex((p) => p[0] === key);
  return from >= 0 && pathBetween(s, from, target.index) !== null;
}

export const mahjongConnect: VariantRules = {
  id: 'mahjong-connect',
  columns: CONNECT_LAYOUTS[0]!.places.map(() => 1), // 144 cells in both grids
  foundations: 1,
  cells: 0,
  decks: 1,
  suits: [],
  variableSuits: 0,
  turn: 1,
  hasWaste: false,
  recycle: false,
  pairs: true,
  tiles: true,
  allFaceUp: true,
  autoMove: false,
  foundationToTableau: false,
  canLift,
  canDrop,
  foundationFor: () => null,
  buildsOn: () => false,
  completedRun: () => false,
  isWon: (s) => s.piles.tableau.every((p) => p.length === 0),
  dealIds,
  layouts: CONNECT_LAYOUTS,
  pairsLeft: joinablePairs,
  reshuffle: shuffledGrid,
};
